// English strings. Keys are dotted by area; '{name}' tokens are interpolated.

export const en = {
  // Connecting / route
  'connecting': 'Connecting…',
  'joiningRoom': 'Joining room {code}…',
  'disconnected': 'disconnected — retrying…',

  // Time
  'time.justNow': 'just now',
  'time.minutesAgo': '{n} min ago',
  'time.hoursAgo': '{n} hr ago',

  // Room types (used everywhere a type is shown)
  'type.games': 'Games',
  'type.movies': 'Movies',
  'type.anime': 'Anime',

  // Home page
  'home.title': '🎲 Totally Unbiased Randomizer',
  'home.subtitle': 'Spin up a room, share the code, let the wheel decide.',
  'home.create': 'Create a room',
  'home.titleLabel': 'Title',
  'home.titlePlaceholder': 'Friday game night',
  'home.typeLabel': 'Type',
  'home.listed': 'List publicly',
  'home.listedHint': '(uncheck for link-only)',
  'home.createButton': 'Create room',
  'home.titleRequired': 'Title is required',
  'home.activeRooms': 'Active rooms',
  'home.codePlaceholder': 'Enter room code',
  'home.join': 'Join',
  'home.loading': 'loading rooms…',
  'home.noRooms': 'No public rooms yet — be the first to create one!',
  'home.players_one': '{n} player',
  'home.players_few': '{n} players',
  'home.players_many': '{n} players',

  // Room — header
  'room.back': '← Home',
  'room.backTitle': 'Back to room list',
  'room.nameLabel': 'Your name',
  'room.namePlaceholder': 'Player',

  // Room — participant card
  'room.you': '(you)',
  'room.statusReady': '✓ ready',
  'room.statusPicking': '…picking',
  'room.statusDisconnected': 'disconnected',
  'room.joining': 'joining…',
  'room.noPicks': 'no picks yet',

  // Room — action buttons
  'room.selectAll': 'Select all',
  'room.deselectAll': 'Deselect all',
  'room.preset': 'Preset',
  'room.presetTitle': 'Apply your saved set',
  'room.save': 'Save',
  'room.saveTitle': 'Save current picks as preset',
  'room.presetSaved': 'Preset saved ({n})',
  'room.ready': 'Ready',
  'room.cancelReady': 'Cancel ready',

  // Room — picks summary
  'room.pickedSummary_games': '{n} picked · {h}/1 ♥',
  'room.pickedSummary_movies': '{n} picked · {h}/1 ♥',
  'room.pickedSummary_anime': '{n} picked · {h}/1 ♥',

  // Room — heart button
  'room.heart.remove': 'Remove heart',
  'room.heart.move': 'Move your heart here (+1 slice)',
  'room.heart.add': 'Heart this pick (+1 slice; 1 per player)',

  // Room — custom add
  'room.customPlaceholder.games': 'Add custom game(s) — one per line',
  'room.customPlaceholder.movies': 'Add custom movie(s) — one per line',
  'room.customPlaceholder.anime': 'Add custom title(s) — one per line',
  'room.add': 'Add',
  'room.removeCustom': 'Remove custom game',

  // Room — vetoed/master list
  'room.master': 'Master list',
  'room.masterEmpty': 'No picks yet — start choosing!',
  'room.vetoed': 'Vetoed',
  'room.vetoedBy': 'by {name}',
  'room.undo': 'Undo',
  'room.undoTitle': 'Restore this game and free your veto',
  'room.footerStatus': '{ready}/{total} ready · {pool} in pool',
  'room.outLabel': 'out',

  // Room — downvote button
  'room.dv.remove': 'Remove your downvote',
  'room.dv.grayedOut': 'Already grayed out by 2 players',
  'room.dv.title': 'Downvote ({n}/2)',

  // Room — vetoed game tooltip suffix
  'room.tile.vetoed': '({name}) (vetoed)',

  // Room — countdown / wheel / result
  'room.spinning': 'Spinning…',
  'room.eyebrow.games': 'tonight we play',
  'room.eyebrow.movies': 'tonight we watch',
  'room.eyebrow.anime': 'tonight we watch',
  'room.startNew': 'Start a new round',
  'room.vetoUsed': 'Veto used',
  'room.vetoButton': 'Veto (1/round)',
  'room.vetoTitle': 'Removes this game and re-spins',
  'room.vetoConfirm': 'Veto {name}? This uses your one veto for the session.',
  'room.requestRespin': 'Request re-spin',
  'room.respinTitle': 'All players must agree',
  'room.respinStatus': 'Re-spin: {voted}/{total} agreed',
  'room.respinYes': 'Yes, re-spin',
  'room.respinNo': 'No, keep result',
  'room.respinWaiting': 'Waiting for others…',

  // Admin shortcut in room header
  'room.admin.reset': '🛠️ Reset',
  'room.admin.resetTitle': 'Admin: reset this room',
  'room.admin.resetConfirm': 'Reset room {code}? All picks/votes/vetoes cleared.',
  'room.admin.resetDone': 'Room reset',
  'room.admin.resetFail': 'Reset failed: {status}',

  // Admin page
  'admin.signIn': 'Admin sign-in',
  'admin.signInHint': 'Paste your admin token to manage catalogs and rooms.',
  'admin.tokenPlaceholder': 'Token',
  'admin.signInButton': 'Sign in',
  'admin.invalidToken': 'Invalid token',
  'admin.authFailed': 'Auth failed ({status})',
  'admin.networkError': 'Network error: {message}',
  'admin.tokenRejected': 'Token rejected — please sign in again.',
  'admin.title': '🛠️ Admin',
  'admin.refresh': '↻ Refresh',
  'admin.signOut': 'Sign out',
  'admin.catalogs': 'Catalogs',
  'admin.loading': 'Loading…',
  'admin.noData': 'No data yet.',
  'admin.upload': 'Upload logos',
  'admin.uploadHint': 'PNG / SVG / WebP / AVIF / JPG (≤5 MB). Filename becomes the display name.',
  'admin.noLogos': 'No logos yet. Upload some above.',
  'admin.delete': 'Delete',
  'admin.deleteLogoConfirm': 'Delete {filename}? Existing rooms of type "{type}" will lose this logo immediately.',
  'admin.deletedLogo': 'Deleted {filename}',
  'admin.deleteLogoFail': 'Delete failed: {detail}',
  'admin.uploadedLogo': 'Uploaded {filename}',
  'admin.uploadFail': 'Upload failed: {detail}',
  'admin.activeRooms': 'Active rooms',
  'admin.loadingRooms': 'Loading rooms…',
  'admin.noActiveRooms': 'No active rooms.',
  'admin.open': 'Open',
  'admin.reset': 'Reset',
  'admin.resetConfirm': 'Reset {title} ({code})? All picks/votes/vetoes cleared.',
  'admin.resetDone': 'Reset {code}',
  'admin.resetFail': 'Reset failed: {status}',
  'admin.deleteRoomConfirm': 'Delete {title} ({code})? All players will be kicked back to the home page.',
  'admin.deletedRoom': 'Deleted {code}',
  'admin.deleteRoomFail': 'Delete failed: {status}',
  'admin.listed': 'listed',
  'admin.unlisted': 'unlisted',
  'admin.catalogFetchFail': 'Catalog fetch failed: {message}',
  'admin.roomsFetchFail': 'Rooms fetch failed: {message}',
};

export type TranslationKey = keyof typeof en;
